import sharedEn from "@/shared/i18n/en";
import sharedDe from "@/shared/i18n/de";
import systemEn from "@system/i18n/en";
import systemDe from "@system/i18n/de";
import designerEn from "@designer/i18n/en";
import designerDe from "@designer/i18n/de";
import operatorEn from "@operator/i18n/en";
import operatorDe from "@operator/i18n/de";

/** Locales the app ships. `en` is the schema source and the fallback. */
export const LOCALES = ["en", "de"] as const;

export type Locale = (typeof LOCALES)[number];

export function isLocale(value: unknown): value is Locale {
	return LOCALES.includes(value as Locale);
}

/**
 * Same key tree as `T`, but every leaf is any string — what a translation of the
 * `en` tree has to look like. Used to hold the other locales to `en`'s shape, so
 * a *missing* key is a compile error instead of a silent runtime fallback.
 *
 * It does not catch a *stray* key: the locales below are built by spreading, and
 * TypeScript's excess-property check doesn't see through a spread. `npm run
 * lint:i18n` is what fails on those — as it is for typo'd keys at the call sites,
 * which vue-i18n's `Key extends string` signature can't reject either.
 */
type Translation<T> = {
	[K in keyof T]: T[K] extends string ? string : Translation<T[K]>;
};

type TopLevelOverlap<A, B> = Extract<keyof A, keyof B>;

/**
 * Composing the locale modules is a shallow spread, so two modules exporting the
 * same top-level namespace would silently drop one of them. This assertion makes
 * that a compile error: if the line below fails, the type in the error message is
 * the colliding namespace — rename it in one of the two modules.
 */
type AssertNoOverlap<Overlap extends never> = Overlap;

type _NamespacesAreDisjoint = AssertNoOverlap<
	| TopLevelOverlap<typeof sharedEn, typeof systemEn>
	| TopLevelOverlap<typeof sharedEn, typeof designerEn>
	| TopLevelOverlap<typeof sharedEn, typeof operatorEn>
	| TopLevelOverlap<typeof systemEn, typeof designerEn>
	| TopLevelOverlap<typeof systemEn, typeof operatorEn>
	| TopLevelOverlap<typeof designerEn, typeof operatorEn>
>;

const en = {
	...sharedEn,
	...systemEn,
	...designerEn,
	...operatorEn,
};

/** The shape of every locale, derived from `en`. Keys are checked against this. */
export type MessageSchema = typeof en;

const de: Translation<MessageSchema> = {
	...sharedDe,
	...systemDe,
	...designerDe,
	...operatorDe,
};

export const messages: Record<Locale, Translation<MessageSchema>> = { en, de };
